import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import {
  Box,
  Chip,
  IconButton,
  Paper,
  Stack,
  Tooltip as MuiTooltip,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { BrandSpinner } from "../../../components/feedback/BrandSpinner";
import { getCurrencies } from "../../../services/currenciesService";
import { getDashboardOverview } from "../../../services/dashboardService";
import { getShops } from "../../../services/shopsService";
import { getMonthToDateRange } from "../../../utils/localDate";
import { AnalyticsFiltersDrawer } from "../../analytics/components/AnalyticsFiltersDrawer";

const chartColors = [
  "#FF8A65",
  "#4DB6AC",
  "#64B5F6",
  "#BA68C8",
  "#FFD54F",
  "#81C784",
  "#F06292",
  "#7986CB",
];

function extractShops(payload) {
  const paginator = payload?.shops ?? payload?.data?.shops ?? payload?.data ?? payload;
  return Array.isArray(paginator?.data)
    ? paginator.data
    : Array.isArray(paginator)
      ? paginator
      : [];
}

function extractCurrencies(payload) {
  const paginator = payload?.currencies ?? payload?.data?.currencies ?? payload?.data ?? payload;
  return Array.isArray(paginator?.data)
    ? paginator.data
    : Array.isArray(paginator)
      ? paginator
      : [];
}

function money(value, currency) {
  return `${currency} ${Number(value || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function MetricCard({ label, value, note }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.7,
        borderRadius: 0,
        minHeight: 112,
        bgcolor: "#FFFFFF",
        borderColor: "#E7EAF0",
      }}
    >
      <Typography sx={{ color: "#667085", fontSize: "0.68rem", fontWeight: 700 }}>
        {label}
      </Typography>
      <Typography sx={{ mt: 0.7, fontSize: "1.55rem", fontWeight: 900 }}>
        {value}
      </Typography>
      <Typography sx={{ mt: 0.45, color: "#98A2B3", fontSize: "0.64rem" }}>
        {note}
      </Typography>
    </Paper>
  );
}

export function DashboardPage() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [filters, setFilters] = useState(() => ({
    ...getMonthToDateRange(),
    shop_ids: [],
    currencies: [],
    group_by: "day",
    shop_ranking: "top",
  }));

  const shopsQuery = useQuery({
    queryKey: ["dashboard-shop-options"],
    queryFn: () => getShops({ page: 1, per_page: 100, is_active: 1 }),
  });

  const currenciesQuery = useQuery({
    queryKey: ["dashboard-currency-options"],
    queryFn: () => getCurrencies({ page: 1, per_page: 100, is_active: 1 }),
  });

  const shops = useMemo(() => extractShops(shopsQuery.data), [shopsQuery.data]);
  const currencies = useMemo(
    () => extractCurrencies(currenciesQuery.data),
    [currenciesQuery.data],
  );

  useEffect(() => {
    if (shops.length && filters.shop_ids.length === 0) {
      setFilters((current) => ({
        ...current,
        shop_ids: shops.map((shop) => shop.id),
      }));
    }
  }, [shops, filters.shop_ids.length]);

  useEffect(() => {
    if (currencies.length && filters.currencies.length === 0) {
      setFilters((current) => ({
        ...current,
        currencies: currencies.map((currency) => currency.code),
      }));
    }
  }, [currencies, filters.currencies.length]);

  const dashboardQuery = useQuery({
    queryKey: ["dashboard-overview", filters],
    queryFn: () => getDashboardOverview(filters),
    enabled: Boolean(
      filters.from_date &&
        filters.to_date &&
        filters.shop_ids.length &&
        filters.currencies.length,
    ),
    refetchInterval: 60000,
  });

  const data = dashboardQuery.data || {};
  const revenue = data.revenue_by_currency || [];
  const trend = data.trend || [];
  const summary = data.summary?.current || {};
    const requestSummary = data.request_summary || {};

  const trendPeriods = useMemo(() => {
    const map = new Map();
    trend.forEach((row) => {
      const item = map.get(row.period) || { period: row.period };
      item[row.currency] = Number(row.revenue || 0);
      map.set(row.period, item);
    });
    return Array.from(map.values());
  }, [trend]);

  const receiptCountByCurrency = useMemo(
    () =>
      revenue.map((row) => ({
        currency: row.currency,
        receipts: Number(row.vouchers || 0),
      })),
    [revenue],
  );

  const shopHeadcount = useMemo(() => {
    const map = new Map();

    (data.shop_performance || []).forEach((row) => {
      const key = row.shop_id || row.shop_code || row.shop_name;
      const current = map.get(key) || {
        shop_id: row.shop_id,
        shop_code: row.shop_code,
        shop_name: row.shop_name || row.shop_code || "Unknown shop",
        vouchers: 0,
      };

      current.vouchers += Number(row.vouchers || 0);
      map.set(key, current);
    });

    return Array.from(map.values()).sort((a, b) => b.vouchers - a.vouchers);
  }, [data.shop_performance]);

  const resetFilters = () => {
    const range = getMonthToDateRange();
    setFilters({
      ...range,
      shop_ids: shops.map((shop) => shop.id),
      currencies: currencies.map((currency) => currency.code),
      group_by: "day",
      shop_ranking: "top",
    });
  };

  if (
    dashboardQuery.isLoading ||
    shopsQuery.isLoading ||
    currenciesQuery.isLoading
  ) {
    return (
      <Box sx={{ minHeight: 420, display: "grid", placeItems: "center" }}>
        <BrandSpinner />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.2, md: 2 }, bgcolor: "#F7F8FA", minHeight: "100%" }}>
      <Stack direction="row" justifyContent="flex-end" spacing={0.4} sx={{ mb: 1.2 }}>
        <MuiTooltip title="Refresh dashboard">
          <IconButton
            size="small"
            onClick={() => dashboardQuery.refetch()}
            sx={{ border: "1px solid #DDE3EA", borderRadius: 0, bgcolor: "#FFFFFF" }}
          >
            <RefreshRoundedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </MuiTooltip>

        <MuiTooltip title="Dashboard filters">
          <IconButton
            size="small"
            onClick={() => setDrawerOpen(true)}
            sx={{ border: "1px solid #DDE3EA", borderRadius: 0, bgcolor: "#FFFFFF" }}
          >
            <FilterAltOutlinedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </MuiTooltip>
      </Stack>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "repeat(4, 1fr)" }, gap: 1.2 }}>
        <MetricCard label="Voucher headcount" value={requestSummary.total || 0} note="Vouchers issued in selected period" />
        <MetricCard label="RADIUS synced" value={summary.radius_synced || 0} note="Successfully synchronised vouchers" />
        <MetricCard label="POS success rate" value={`${data.request_summary?.success_rate || 0}%`} note={`${data.request_summary?.successful || 0} successful · ${data.request_summary?.failed || 0} failed`} />
        <MetricCard label="Selected coverage" value={`${filters.shop_ids.length}/${shops.length}`} note="Authorised shops included" />
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1.35fr 0.65fr" }, gap: 1.2, mt: 1.2 }}>
        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 0, minHeight: 360 }}>
          <Typography sx={{ fontSize: "0.78rem", fontWeight: 850 }}>Revenue movement</Typography>
          <Box sx={{ height: 300, mt: 1 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trendPeriods}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EAECF0" />
                <XAxis dataKey="period" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend />
                {filters.currencies.map((currency, index) => (
                  <Line
                    key={currency}
                    type="monotone"
                    dataKey={currency}
                    stroke={chartColors[index % chartColors.length]}
                    strokeWidth={3}
                    dot={{ r: 4 }}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </Paper>

        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 0, minHeight: 360 }}>
          <Typography sx={{ fontSize: "0.78rem", fontWeight: 850 }}>Revenue by currency</Typography>
          <Box sx={{ height: 220, mt: 1 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={revenue.map((row) => ({ name: row.currency, value: Number(row.revenue) }))}
                  dataKey="value"
                  nameKey="name"
                  outerRadius={82}
                  label
                >
                  {revenue.map((row, index) => (
                    <Cell key={row.currency} fill={chartColors[index % chartColors.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value, name) => money(value, name)} />
              </PieChart>
            </ResponsiveContainer>
          </Box>
          <Stack spacing={0.7}>
            {revenue.map((row, index) => (
              <Box key={row.currency} sx={{ p: 0.9, borderLeft: `4px solid ${chartColors[index % chartColors.length]}`, bgcolor: "#FAFBFC" }}>
                <Typography sx={{ fontSize: "0.72rem", fontWeight: 850 }}>{money(row.revenue, row.currency)}</Typography>
                <Typography sx={{ color: "#667085", fontSize: "0.62rem" }}>{row.vouchers} receipts · Avg {money(row.average_transaction, row.currency)} · {row.change_percent >= 0 ? "+" : ""}{row.change_percent}%</Typography>
              </Box>
            ))}
          </Stack>
        </Paper>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: 1.2, mt: 1.2 }}>
        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 0, minHeight: 360 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography sx={{ fontSize: "0.78rem", fontWeight: 850 }}>{filters.shop_ranking === "top" ? "Top 10" : "Bottom 10"} shops by revenue</Typography>
            <Chip label={filters.shop_ranking === "top" ? "Best performing" : "Needs attention"} size="small" />
          </Stack>
          <Box sx={{ height: 300, mt: 1 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.shop_performance || []} layout="vertical" margin={{ left: 12, right: 24 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EAECF0" />
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="shop_name" width={180} tick={{ fontSize: 10 }} />
                <Tooltip formatter={(value, _, item) => money(value, item.payload.currency)} />
                <Bar dataKey="revenue" fill="#64B5F6" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Paper>

        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 0, minHeight: 360 }}>
          <Typography sx={{ fontSize: "0.78rem", fontWeight: 850 }}>Shop headcount comparison</Typography>
          <Typography sx={{ mt: 0.25, color: "#667085", fontSize: "0.64rem" }}>Headcount is the number of vouchers issued per shop.</Typography>
          <Box sx={{ height: 300, mt: 1 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={shopHeadcount} layout="vertical" margin={{ left: 12, right: 24 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EAECF0" />
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                <YAxis
                  type="category"
                  dataKey="shop_name"
                  width={190}
                  interval={0}
                  tick={{ fontSize: 10 }}
                />
                <Tooltip
                  formatter={(value) => [`${value} vouchers`, "Headcount"]}
                  labelFormatter={(label) => label}
                />
                <Bar dataKey="vouchers" fill="#FF8A65" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Paper>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "0.8fr 1.2fr" }, gap: 1.2, mt: 1.2 }}>
        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 0, minHeight: 340 }}>
          <Typography sx={{ fontSize: "0.78rem", fontWeight: 850 }}>Receipt count by currency</Typography>
          <Typography sx={{ mt: 0.25, color: "#667085", fontSize: "0.64rem" }}>This compares transaction count, not monetary denominations.</Typography>
          <Box sx={{ height: 270, mt: 1 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={receiptCountByCurrency}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EAECF0" />
                <XAxis dataKey="currency" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(value) => [`${value} receipts`, "Receipt count"]} />
                <Bar dataKey="receipts" radius={[6, 6, 0, 0]}>
                  {receiptCountByCurrency.map((row, index) => (
                    <Cell key={row.currency} fill={chartColors[index % chartColors.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Paper>

        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 0, minHeight: 340 }}>
          <Typography sx={{ fontSize: "0.78rem", fontWeight: 850 }}>Package performance</Typography>
          <Box sx={{ height: 285, mt: 1 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.package_performance || []} margin={{ left: 8, right: 16 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EAECF0" />
                <XAxis dataKey="package_name" tick={{ fontSize: 10 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend />
                <Bar dataKey="vouchers" name="Vouchers" fill="#4DB6AC" radius={[6, 6, 0, 0]} />
                <Bar dataKey="used" name="Used" fill="#BA68C8" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Paper>
      </Box>

      <AnalyticsFiltersDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        filters={filters}
        setFilters={setFilters}
        shops={shops}
        currencies={currencies}
        onApply={() => dashboardQuery.refetch()}
        onReset={resetFilters}
      />
    </Box>
  );
}
